import { useMemo } from "react";
import {
  useV2ListApprovedServicesForAccountQuery,
  useV2ListServicesForAccountQuery,
  V2ListApprovedServicesForAccountApiArg,
  V2ListServicesForAccountApiArg,
  V2ListServicesForAccountApiResponse,
} from "@generated";
import useFlags from "@common/hooks/useFlags";
import { normalizeApproved, translateInclude } from "./normalizeApproved";

export const useApprovedCatalog = (): boolean => {
  const { releaseApprovedServices } = useFlags();
  return releaseApprovedServices === true;
};

// Lists catalog services from /v2/services, or from /v2/approved-services when
// release.approved-services is on. Both hooks are always called (rules of
// hooks); the unused one is skipped so only one request is made. Approved
// results are normalized to the /v2/services shape.
export const useListCatalogServices = (
  args: V2ListServicesForAccountApiArg,
  opts: { skip?: boolean } = {}
) => {
  const approved = useApprovedCatalog();

  const live = useV2ListServicesForAccountQuery(args, {
    skip: !!opts.skip || approved,
  });
  const snapshot = useV2ListApprovedServicesForAccountQuery(
    {
      ...args,
      include: translateInclude(args.include),
    } as unknown as V2ListApprovedServicesForAccountApiArg,
    { skip: !!opts.skip || !approved }
  );

  const source = approved ? snapshot : live;
  const data = useMemo(
    () =>
      (approved ? normalizeApproved(source.data) : source.data) as
        | V2ListServicesForAccountApiResponse
        | undefined,
    [approved, source.data]
  );

  return {
    data,
    error: source.error,
    isLoading: source.isLoading,
    isFetching: source.isFetching,
    refetch: source.refetch,
    approved,
  };
};
